import { ApiError, NetworkError, toApiError } from './problem';

/**
 * Everything the client needs to know that it cannot work out for itself.
 *
 * Supplied by the session layer in 1.7.4 rather than imported from it, so this module stays free of
 * React and of any particular storage choice — which is what makes it testable without a browser.
 */
export interface ApiClientContext {
  /** The current access token, or null when signed out. Held in memory, never persisted. */
  getAccessToken: () => string | null;

  /**
   * The branch the user is acting at, sent as `X-Branch-Id`.
   *
   * Client-supplied and therefore always validated server-side against the user's assignments —
   * this header selects among rights they already hold, it never grants any.
   */
  getBranchId: () => number | null;

  /**
   * The API serving the current company, or null for this app's own origin.
   *
   * Resolved by the company step of sign-in (see `session/tenantEndpoint`), because a tenant may
   * be served by its own deployment. Null is the normal case and keeps every request on the
   * relative paths described below.
   */
  getApiBaseUrl?: () => string | null;

  /**
   * The app's own API, used where a request has no tenant base URL — always for `discovery`, and
   * for everything else while the tenant's is null.
   *
   * Null on the web, where a relative path already means the site the page came from. The mobile
   * app has no page, so a relative path goes nowhere: it supplies the server's address here, and
   * without it resolving a company code could never succeed on a phone.
   */
  getOrigin?: () => string | null;

  /**
   * Called when the API says the session is gone — a 401 on a call that actually carried one.
   *
   * Never fired for an `anonymous` request. Sign-in and refresh answer 401 to mean "wrong
   * credentials", and treating that as an expired session tells someone mistyping their password
   * that they have been signed out of a session they were never in.
   */
  onUnauthenticated?: () => void;

  /**
   * Headers this client sends on every request, after the ones above.
   *
   * The mobile app sends `X-AttireRent-Client: native` here, which is what makes the API return the
   * refresh token in the body instead of a cookie. The web sends nothing.
   */
  getHeaders?: () => Record<string, string>;

  /**
   * Whether cookies travel. `include` (the default) is the web's: its refresh token is an HttpOnly
   * cookie. The mobile app sets `omit` — its token is in the device's secure store, and iOS keeps a
   * native cookie jar that would otherwise send a cookie nobody meant to keep.
   */
  credentials?: 'include' | 'omit';

  /**
   * Gets a fresh access token after a 401, resolving true when one is now in hand.
   *
   * The mobile app sets this: an access token lives thirty minutes, and a clerk signed out every
   * half hour would stop using the phone. A request that carried a session and was refused is
   * retried **once** after a successful refresh; a second refusal, or a refresh that fails, falls
   * through to {@link onUnauthenticated} as before. The web sets nothing, so a 401 still ends its
   * session exactly as it did.
   *
   * <p>
   * The implementation must share one refresh between concurrent callers. Refreshing rotates the
   * token, and the server answers a second use of the token it replaced by revoking the whole
   * session, so two requests expiring together must not each refresh.
   * </p>
   */
  refreshAccessToken?: () => Promise<boolean>;
}

const defaultContext: ApiClientContext = {
  getAccessToken: () => null,
  getBranchId: () => null,
};

let context: ApiClientContext = defaultContext;

export function configureApiClient(next: ApiClientContext): void {
  context = next;
}

/** Test seam — restores the unauthenticated defaults. */
export function resetApiClient(): void {
  context = defaultContext;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;

  /**
   * Skip the `Authorization` header. Only sign-in and refresh want this — everything else should
   * send the token it has.
   */
  anonymous?: boolean;

  /**
   * Ignore the tenant's API base URL and call this app's own origin.
   *
   * Exactly one call needs this, and it is the one that cannot do otherwise: resolving a company
   * code is what *produces* the base URL, so it can never be sent to it. Anything else setting
   * this would be quietly addressing the wrong server.
   */
  discovery?: boolean;
}

/**
 * The single door to the API.
 *
 * Everything goes through here so that the cross-cutting concerns exist in exactly one place: the
 * bearer token, the acting branch, cookie credentials, JSON handling, and turning a failure into a
 * typed {@link ApiError} that still carries the server's own message. A component that reaches for
 * `fetch` directly bypasses all of it, which is how a screen ends up rendering "[object Object]"
 * where an explanation should be.
 *
 * The path stays relative by default. In development the Vite dev server proxies `/api`, and in a
 * single-deployment production the app is served from the same origin — both deliberately, because
 * a single origin is what lets the refresh cookie stay `SameSite=Lax`.
 *
 * A tenant served by its own API deployment overrides that with an absolute base URL from the
 * company step of sign-in. That arrangement is cross-site, so it needs `SameSite=None` on the
 * refresh cookie, the API's origin allow-list populated, and the Origin guard the API runs in that
 * configuration — the cookie stops defending itself the moment the two are not one site.
 */
export function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return send<T>(path, options, true);
}

async function send<T>(path: string, options: RequestOptions, mayRefresh: boolean): Promise<T> {
  const { method = 'GET', body, signal, anonymous = false, discovery = false } = options;

  const headers = new Headers({ Accept: 'application/json' });

  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  if (!anonymous) {
    const token = context.getAccessToken();

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const branchId = context.getBranchId();

    if (branchId !== null) {
      headers.set('X-Branch-Id', String(branchId));
    }
  }

  for (const [name, value] of Object.entries(context.getHeaders?.() ?? {})) {
    headers.set(name, value);
  }

  const origin = context.getOrigin?.() ?? null;
  const base = discovery ? origin : (context.getApiBaseUrl?.() ?? origin);
  const url = base === null ? path : `${base}${path}`;

  let response: Response;

  try {
    response = await fetch(url, {
      method,
      headers,
      // The web's refresh token is an HttpOnly cookie, so it only travels when credentials are
      // included.
      credentials: context.credentials ?? 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    // An abort is the caller changing its mind, not a failure — rethrow it untouched so callers
    // and TanStack Query can recognise it. Tested by name rather than `instanceof DOMException`:
    // React Native has no DOMException, and naming an undefined global there is a ReferenceError
    // that would turn every cancelled request into a crash.
    if (isAbort(error)) {
      throw error;
    }

    throw new NetworkError(error);
  }

  if (!response.ok) {
    const error = await toApiError(response);

    // Only for a call that carried a session. An anonymous one — signing in, or exchanging the
    // refresh cookie — answers 401 to mean "these credentials are wrong", which is not the same
    // thing as "the session you had has ended". Without this distinction a mistyped password
    // announces "Your session has expired. Sign in again." to somebody who is already on the
    // sign-in screen doing exactly that, alongside the real reason.
    if (error.isUnauthenticated && !anonymous) {
      // Once only: the retry is sent with `mayRefresh` false, so a token the server still refuses
      // ends the session rather than looping between refresh and request.
      if (mayRefresh && (await tryRefresh())) {
        return send<T>(path, options, false);
      }

      context.onUnauthenticated?.();
    }

    throw error;
  }

  return (await readBody<T>(response)) as T;
}

/** A refresh that throws is a refresh that failed — the session ends, the caller sees the 401. */
async function tryRefresh(): Promise<boolean> {
  if (!context.refreshAccessToken) {
    return false;
  }

  try {
    return await context.refreshAccessToken();
  } catch {
    return false;
  }
}

function isAbort(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * 204 is the API's answer for a successful command with nothing to say — go-live, logout, a
 * delete. Calling `.json()` on that throws, so the absence has to be handled here rather than at
 * every call site.
 */
async function readBody<T>(response: Response): Promise<T | undefined> {
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined;
  }

  const text = await response.text();

  return text ? (JSON.parse(text) as T) : undefined;
}

export { ApiError, NetworkError };
